import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Box from 'shopper/components/Box';
import Spinner from 'shopper/components/Spinner';

import Pagination from 'components/Pagination';

import { sendEvent } from 'lib/gtag';
import { toLastCommentsLink } from 'lib/links';
import { commentQueryKeys } from 'lib/queryKeys';

import {
  getArticleLastCommentsList,
  getLastCommentsList,
  getOfferLastCommentsList,
  getThreadLastCommentsList,
} from 'services/comment';

import LastCommentsListItem from './ListItem';

const COMMENTS_LIMIT = 20;
const GA_EVENT_CATEGORY = 'last_comments';

const ENDPOINTS = {
  offers: getOfferLastCommentsList,
  articles: getArticleLastCommentsList,
  threads: getThreadLastCommentsList,
  any: getLastCommentsList,
};

const onPaginationFirstButtonClick = () =>
  sendEvent({ category: GA_EVENT_CATEGORY, action: 'first_page' });

const onPaginationLastButtonClick = () =>
  sendEvent({ category: GA_EVENT_CATEGORY, action: 'last_page' });

const onPaginationItemsClick = () =>
  sendEvent({ category: GA_EVENT_CATEGORY, action: 'select_number' });

const LastCommentsList = ({ commentsSource, commentsPage = null }) => {
  const router = useRouter();

  const {
    data: comments,
    isLoading,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: commentQueryKeys.list({ commentsSource, commentsPage }),
    queryFn: ({ signal }) =>
      ENDPOINTS[commentsSource.name](
        {
          limit: COMMENTS_LIMIT,
          page: commentsPage,
        },
        { signal }
      ),
    useErrorBoundary: true,
  });

  const onPaginationChange = (page) =>
    router.push(toLastCommentsLink(commentsSource?.slug, page));

  return (
    <Box full>
      <div className="w-full px-4 py-2 md:px-6">
        {isSuccess &&
          comments.map((comment) => (
            <LastCommentsListItem key={comment.commentId} {...comment} />
          ))}
        {(isLoading || isFetching) && (
          <div className="flex justify-center pb-12 pt-14 md:pt-18">
            <Spinner />
          </div>
        )}
      </div>
      {isSuccess && !isLoading && !isFetching && (
        <div className="flex justify-center p-4 md:justify-end md:p-6">
          <Pagination
            hrefFn={(page) => toLastCommentsLink(commentsSource.slug, page)}
            page={commentsPage || 1}
            total={50}
            onChange={onPaginationChange}
            onFirstButtonClick={onPaginationFirstButtonClick}
            onItemsClick={onPaginationItemsClick}
            onLastButtonClick={onPaginationLastButtonClick}
          />
        </div>
      )}
    </Box>
  );
};

LastCommentsList.propTypes = {
  commentsSource: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string,
  }).isRequired,
  commentsPage: PropTypes.number,
};

export default memo(LastCommentsList);
